import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type SubscriptionJob } from '../services/api'

const labels = { expiration: 'Expiration cleanup', provider: 'Subscription refresh' }
const states = { idle: 'Not run yet', pending: 'Queued', running: 'Running', completed: 'Completed', failed: 'Finished with errors' }

export default function SubscriptionMaintenance() {
  const cache = useQueryClient()
  const status = useQuery({
    queryKey: ['subscription-jobs'],
    queryFn: () => api.getSubscriptionJobs(),
    refetchInterval: 5000,
  })
  const run = useMutation({
    mutationFn: (kind: SubscriptionJob['kind']) => api.runSubscriptionJob(kind),
    onSettled: () => cache.invalidateQueries({ queryKey: ['subscription-jobs'] }),
  })
  const busy = run.isPending || status.data?.jobs.some(job => ['pending', 'running'].includes(job.status))

  return (
    <section className="card bg-base-100" aria-label="Subscription maintenance">
      <div className="card-body">
        <h2 className="card-title">Subscription maintenance</h2>
        <p className="text-sm text-base-content/60">These actions update access for all organizations and individuals.</p>
        {status.isPending && <p role="status">Loading maintenance status…</p>}
        {status.error && <p role="alert" className="text-error">{status.error.message}</p>}
        {run.error && <p role="alert" className="text-error">{run.error.message}</p>}
        <div className="grid gap-4 md:grid-cols-2">
          {status.data?.jobs.map(job => (
            <div key={job.kind} className="rounded-box border border-base-300 p-4 space-y-2">
              <h3 className="font-semibold">{labels[job.kind]}</h3>
              <p className="text-sm text-base-content/70">
                {job.kind === 'expiration'
                  ? `Daily after midnight (${job.timezone}). Removes access based on saved expiration dates.`
                  : `Every ${status.data.provider_interval_seconds / 60} minutes. Checks Re:Members for voids and renewal activation; may also discover expired subscriptions.`}
              </p>
              <p className="text-sm" role="status">{states[job.status]}{job.error_count > 0 ? ` · ${job.error_count} error(s); some records may need attention.` : ''}</p>
              {job.requested_at && <p className="text-xs text-base-content/60">Last requested: {new Date(job.requested_at).toLocaleString()}{job.requested_by === 'scheduled' ? ' (scheduled)' : ''}</p>}
              {job.finished_at && <p className="text-xs text-base-content/60">Finished: {new Date(job.finished_at).toLocaleString()}</p>}
              <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => run.mutate(job.kind)}>
                {job.kind === 'expiration' ? 'Run expiration cleanup' : 'Refresh subscriptions'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
